/**
 * Manages testimonial display and loading functionality for the wall of love page.
 */
class TestimonialsWaves {
    /**
     * @param {HTMLElement} section - The main section of the testimonials waves
     * @param {HTMLElement} container - The main container element for testimonials waves
     */
    constructor(section, container) {
        this.section = section;
        this.container = container;
        this.testimonialConfigs = []; // Array of {type, wavesPerColumn, initialVisibleWaves}
        this.currentVisibleWaves = 0; // Track how many waves are currently visible beyond initial
        this.loadMoreBtn = section.querySelector('button[data-load-more]');

        this.init();
    }

    init() {
        if (!this.parseConfig()) {
            return;
        }

        this.setupLoadMoreButton();
        this.updateLoadMoreButtonVisibility();
    }

    /**
     * Parses the data-testimonial-config attribute
     * Supports both single config object and array of configs for multi-type sections
     * @returns {boolean} Success status
     */
    parseConfig() {
        const configAttribute = this.container.dataset.wavesConfig;

        if (!configAttribute) {
            return false;
        }

        try {
            const parsed = JSON.parse(configAttribute);
            this.testimonialConfigs = Array.isArray(parsed) ? parsed : [parsed];
            return true;
        } catch (error) {
            return false;
        }
    }

    setupLoadMoreButton() {
        if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener('click', () => this.loadMore());
        }
    }

    loadMore() {
        // Show next wave for all configured testimonial types
        for (const config of this.testimonialConfigs) {
            const nextWaveToShow = config.initialVisibleWaves + this.currentVisibleWaves;

            // Only show if there are more waves available
            if (nextWaveToShow < config.wavesPerColumn) {
                this.showWave(config.type, nextWaveToShow);
            }
        }

        this.currentVisibleWaves += 1;
        this.updateLoadMoreButtonVisibility();
    }

    /**
     * Shows all testimonials of a specific type at a given wave index
     * @param {string} type - Type of testimonial (e.g., 'members', 'brands', 'linkedin')
     * @param {number} waveIndex - Wave index to show
     */
    showWave(type, waveIndex) {
        const items = this.section.querySelectorAll(`[data-type="${type}"][data-wave="${waveIndex}"]`);

        items.forEach(item => {
            // Remove hidden class and add animation classes
            item.classList.remove('hidden');
            item.classList.add('opacity-0', 'translate-y-10');

            // Use requestAnimationFrame to ensure the opacity and transform are applied
            // before we remove them for the animation
            requestAnimationFrame(() => {
                item.classList.add('transition-all', 'duration-800', 'ease-out');
                item.classList.remove('opacity-0', 'translate-y-10');
            });
        });
    }

    updateLoadMoreButtonVisibility() {
        // Only update visibility if the load more button exists
        if (!this.loadMoreBtn) {
            return;
        }

        // Check if any testimonial type has more waves to show
        let hasMoreWaves = false;

        for (const config of this.testimonialConfigs) {
            const totalVisibleWaves = config.initialVisibleWaves + this.currentVisibleWaves;

            if (totalVisibleWaves < config.wavesPerColumn) {
                hasMoreWaves = true;
                break;
            }
        }

        // Hide button if there are no more waves to load
        this.loadMoreBtn.classList.toggle('hidden', !hasMoreWaves);
    }
}

/**
 * Initialize testimonials flow when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
    const wavesSections = document.querySelectorAll('.js-testimonialsWavesSection');

    wavesSections.forEach(wavesSection => {
        const wavesContainer = /** @type {HTMLElement | null} */ (wavesSection.querySelector('[data-waves-config]'));

        if (wavesContainer) {
            new TestimonialsWaves(
                /** @type {HTMLElement} */ (wavesSection),
                wavesContainer
            );
        }
    });
});
